import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { SiteData, SimulationStatistic } from "../types";
import { useState, useMemo, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  Calendar, 
  Users, 
  Activity, 
  Database, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  FileText,
  BarChart3,
  ExternalLink,
  Phone
} from 'lucide-react';
import logoRed from '/lovable-uploads/2921f24e-cc32-4545-9480-f5f65fe6fe97.png';
import { formatSafeDate, isValidDate } from '../utils/dateUtils';

interface SiteDetailModalProps {
  site: SiteData | null;
  isOpen: boolean;
  onClose: () => void;
  simulationStats: SimulationStatistic[];
}

export const SiteDetailModal = ({ site, isOpen, onClose, simulationStats }: SiteDetailModalProps) => {
  if (!site) return null;

  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  // Generate horizontal bar chart image using canvas
  const generateBarChartImage = async (data: { text: string; value: number }[]) => {
    return new Promise<string>((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 600;
      canvas.height = 450;
      
      if (!ctx) {
        resolve('');
        return;
      }

      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Sort and filter data with simulator info
      const sortedData = [...data]
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10); // Show top 10 modules

      if (sortedData.length === 0) {
        resolve('');
        return;
      }

      // Get simulator info for each module
      const dataWithSimulator = sortedData.map(item => {
        const stat = siteSimulationStats.find(s => s.moduleDescription === item.text);
        return {
          ...item,
          simulator: stat?.simulatorType || 'Unknown'
        };
      });

      const margin = { top: 50, right: 80, bottom: 20, left: 220 };
      const chartWidth = canvas.width - margin.left - margin.right;
      const chartHeight = canvas.height - margin.top - margin.bottom;
      const barHeight = Math.max(25, chartHeight / dataWithSimulator.length - 8);
      const maxValue = Math.max(...dataWithSimulator.map(d => d.value));

      // Draw chart title with more space
      ctx.font = 'bold 14px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#111827';
      ctx.fillText('Sessions by Module (Last 12 Months)', canvas.width / 2, 20);

      // Draw bars and labels
      dataWithSimulator.forEach((item, index) => {
        const y = margin.top + index * (barHeight + 8);
        const barWidth = (item.value / maxValue) * chartWidth;
        
        // Clean module name and simulator name
        const moduleName = item.text.replace(/.*?- /, '').substring(0, 35);
        const simulatorName = item.simulator.substring(0, 20);
        
        // Draw module name (main label)
        ctx.font = '11px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#374151';
        ctx.fillText(moduleName, margin.left - 10, y + barHeight / 2 - 2);
        
        // Draw simulator name (smaller, below module name)
        ctx.font = '9px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#6B7280';
        ctx.fillText(simulatorName, margin.left - 10, y + barHeight / 2 + 2);
        
        // Draw bar
        ctx.fillStyle = '#4F46E5';
        ctx.fillRect(margin.left, y, barWidth, barHeight);
        
        // Draw value at end of bar
        ctx.font = '11px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#374151';
        ctx.fillText(item.value.toString(), margin.left + barWidth + 5, y + barHeight / 2);
      });

      resolve(canvas.toDataURL('image/png'));
    });
  };

  // Filter simulation stats for current site
  const siteSimulationStats = useMemo(() => {
    return simulationStats.filter(stat => stat.siteName === site.siteName);
  }, [simulationStats, site.siteName]);

  // Generate bar chart data from modules
  const barChartData = useMemo(() => {
    const moduleStats = siteSimulationStats.reduce((acc, stat) => {
      if (!acc[stat.moduleDescription]) {
        acc[stat.moduleDescription] = 0;
      }
      acc[stat.moduleDescription] += stat.sessionsLast12Months;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(moduleStats).map(([text, value]) => ({
      text,
      value
    }));
  }, [siteSimulationStats]);

  // Generate simulator usage data
  const simulatorUsage = useMemo(() => {
    const simulatorStats = siteSimulationStats.reduce((acc, stat) => {
      if (!acc[stat.simulatorType]) {
        acc[stat.simulatorType] = {
          simulator: stat.simulatorType,
          sessions: 0,
          modules: []
        };
      }
      acc[stat.simulatorType].sessions += stat.sessionsLast12Months;
      acc[stat.simulatorType].modules.push({
        module: stat.moduleDescription,
        sessions: stat.sessionsLast12Months
      });
      return acc;
    }, {} as Record<string, any>);

    return Object.values(simulatorStats);
  }, [siteSimulationStats]);

  const exportToPDF = async () => {
    if (!pdfContentRef.current) return;
    
    setIsExportingPDF(true);
    
    try {
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;

      // Add header with logo and customer name
      pdf.setFillColor(220, 53, 69); // Red color for header
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add customer name
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(site.customer, margin, 25);
      
      // Add site name
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text(site.siteName, margin, 35);

      // Add creation date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const creationDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      pdf.text(`Report Generated: ${creationDate}`, margin, pageHeight - 10);

      // Add logo in upper right corner
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoRed;
        await new Promise((resolve) => {
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            const imgData = canvas.toDataURL('image/png');
            
            // Calculate aspect ratio to prevent stretching
            const imgAspectRatio = img.width / img.height;
            const maxWidth = 35;
            const maxHeight = 25;
            
            let logoWidth = maxWidth;
            let logoHeight = maxWidth / imgAspectRatio;
            
            if (logoHeight > maxHeight) {
              logoHeight = maxHeight;
              logoWidth = maxHeight * imgAspectRatio;
            }
            
            pdf.addImage(imgData, 'PNG', pageWidth - margin - logoWidth, 8, logoWidth, logoHeight);
            resolve(null);
          };
        });
      } catch (error) {
        console.error('Error loading logo:', error);
        // Fallback text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.text('Surgical Science', pageWidth - margin - 40, 25, { align: 'right' });
      }

      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      let yPosition = 60;

      // Usage Statistics Section
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Usage Statistics', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      
      // Get unique simulator types for this site
      const siteSimulatorTypes = [...new Set(siteSimulationStats.map(stat => stat.simulatorType))];
      
      const usageStats = [
        `Sessions Last Month: ${site.numSessionsLastMonth}`,
        `Sessions Last 12 Months: ${site.numSessionsOnServerLast12Month}`,
        `Active Users: ${site.activeUsers}`,
        `Registered Users: ${site.registeredUsers}`
      ];

      usageStats.forEach(stat => {
        pdf.text(stat, margin, yPosition);
        yPosition += 6;
      });

       // Handle simulator types with proper line wrapping
       if (siteSimulatorTypes.length > 0) {
         const simulatorTypesText = `Simulator Types: ${siteSimulatorTypes.join(', ')}`;
         const maxLineWidth = pageWidth - 2 * margin;
         
         // Check if the text fits on one line
         const textWidth = pdf.getTextWidth(simulatorTypesText);
         
         if (textWidth <= maxLineWidth) {
           pdf.text(simulatorTypesText, margin, yPosition);
           yPosition += 6;
         } else {
           // Split into multiple lines
           pdf.text('Simulator Types:', margin, yPosition);
           yPosition += 6;
           
           let currentLine = '';
           siteSimulatorTypes.forEach((simType, index) => {
             const testLine = currentLine + (currentLine ? ', ' : '  ') + simType;
             const testWidth = pdf.getTextWidth(testLine);
             
             if (testWidth <= maxLineWidth) {
               currentLine = testLine;
             } else {
               if (currentLine) {
                 pdf.text(currentLine, margin, yPosition);
                 yPosition += 6;
               }
               currentLine = '  ' + simType;
             }
             
             // Last item or need to print remaining
             if (index === siteSimulatorTypes.length - 1 && currentLine) {
               pdf.text(currentLine, margin, yPosition);
               yPosition += 6;
             }
           });
         }
       }

      yPosition += 15;

      // Module Usage Bar Chart Section
      if (barChartData.length > 0) {
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Module Usage Distribution', margin, yPosition);
        yPosition += 15;

        // Generate bar chart image
        try {
          const barChartImage = await generateBarChartImage(barChartData);
          pdf.addImage(barChartImage, 'PNG', margin, yPosition, pageWidth - 2 * margin, 150);
          yPosition += 160;
        } catch (error) {
          console.error('Error generating bar chart:', error);
          // Fallback to text representation
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text('Module Usage (Sessions Last 12 Months):', margin, yPosition);
          yPosition += 10;
        }

        // Add new page for detailed statistics
        pdf.addPage();
        yPosition = margin + 20;

        // Module Usage Summary Table
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Module Usage Summary', margin, yPosition);
        yPosition += 15;

        // Filter and sort simulation statistics for this site
        const siteSimStats = simulationStats
          .filter(stat => stat.siteName === site.siteName && stat.sessionsLast12Months > 0)
          .sort((a, b) => b.sessionsLast12Months - a.sessionsLast12Months);

        if (siteSimStats.length > 0) {
          // Table headers
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          const colWidths = [45, 120, 30];
          const headers = ['Simulator', 'Module', 'Sessions'];
          let xPos = margin;
          
          // Draw header background - with more right margin
          pdf.setFillColor(240, 240, 240);
          const totalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
          pdf.rect(margin, yPosition - 5, totalTableWidth + 5, 12, 'F');
          
          pdf.setTextColor(0, 0, 0);
          headers.forEach((header, i) => {
            pdf.text(header, xPos + 2, yPosition + 3);
            xPos += colWidths[i];
          });
          yPosition += 15;

          // Table data
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          
          siteSimStats.forEach((stat, index) => {
            if (yPosition > pageHeight - 30) {
              pdf.addPage();
              yPosition = margin + 20;
              
              // Add creation date on each page
              pdf.setTextColor(0, 0, 0);
              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'normal');
              pdf.text(`Report Generated: ${creationDate}`, margin, pageHeight - 10);
              
              // Repeat headers on new page
              pdf.setFont('helvetica', 'bold');
              pdf.setFillColor(240, 240, 240);
              const totalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
              pdf.rect(margin, yPosition - 5, totalTableWidth + 5, 12, 'F');
              xPos = margin;
              headers.forEach((header, i) => {
                pdf.text(header, xPos + 2, yPosition + 3);
                xPos += colWidths[i];
              });
              yPosition += 15;
              pdf.setFont('helvetica', 'normal');
            }
            
            // Alternate row colors - with consistent right margin
            if (index % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              const totalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
              pdf.rect(margin, yPosition - 4, totalTableWidth + 5, 10, 'F');
            }
            
            xPos = margin;
            pdf.text(stat.simulatorType.substring(0, 15), xPos + 2, yPosition + 2);
            xPos += colWidths[0];
            pdf.text(stat.moduleDescription.substring(0, 35), xPos + 2, yPosition + 2);
            xPos += colWidths[1];
            pdf.text(stat.sessionsLast12Months.toString(), xPos + 2, yPosition + 2);
            
            yPosition += 10;
          });
        } else {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text('No session data available for the last 12 months.', margin, yPosition);
        }

        yPosition += 15;
      }

      // Save the PDF
      pdf.save(`${site.customer}_${site.siteName}_Report.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatSafeDate(dateString, 'N/A');
  };

  const isExpiringSoon = () => {
    if (!isValidDate(site.siteLicenseExpirationInML)) return false;
    const expirationDate = new Date(site.siteLicenseExpirationInML);
    const now = new Date();
    const monthsUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30);
    return monthsUntilExpiration < 3;
  };

  const getStatusIcon = (isOnline: boolean) => {
    return isOnline ? (
      <CheckCircle className="w-4 h-4 text-success" />
    ) : (
      <XCircle className="w-4 h-4 text-destructive" />
    );
  };

  // Suggested actions functions
  const generateCustomerReport = () => {
    const reportData = [{
      Site: site.siteName,
      Customer: site.customer,
      Status: site.isOnline ? 'Online' : 'Offline',
      'Sessions (Last Month)': site.numSessionsLastMonth,
      'Active Users': site.activeUsers,
      'License Expiry': site.siteLicenseExpirationInML
    }];
    
    const csvContent = [
      Object.keys(reportData[0]).join(','),
      ...reportData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${site.customer}_${site.siteName}_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const scheduleCheckupCall = () => {
    const subject = encodeURIComponent(`MentorLearn Cloud check-up call with ${site.customer}`);
    const body = encodeURIComponent(`Hi,\n\nI hope this email finds you well. I wanted to reach out to schedule a check-up call regarding your MentorLearn usage at ${site.siteName} and discuss any questions or feedback you might have.\n\nPlease let me know your availability for the coming week.\n\nBest regards`);
    
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  const openDynamicsCRM = () => {
    const dynamicsUrl = `https://surgicalscienceprod.crm4.dynamics.com/main.aspx?appid=66fefb0e-2cb2-ec11-9840-00224883c6a6&forceUCI=1&pagetype=entitylist&etn=account&viewid=00000000-0000-0000-00aa-000010001002&viewType=1039`;
    window.open(dynamicsUrl, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pr-12">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl flex items-center gap-3">
              {getStatusIcon(site.isOnline)}
              {site.siteName}
              <Badge variant={site.isOnline ? "default" : "destructive"}>
                {site.isOnline ? 'Online' : 'Offline'}
              </Badge>
            </DialogTitle>
            <Button 
              onClick={exportToPDF} 
              disabled={isExportingPDF}
              className="flex items-center gap-2 mr-8"
            >
              {isExportingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export PDF Report
                </>
              )}
            </Button>
          </div>
        </DialogHeader>

        {/* Suggested Actions */}
        <div className="border-b pb-4 mb-6">
          <h3 className="text-lg font-semibold mb-3">Suggested Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={generateCustomerReport}
              variant="outline" 
              className="flex items-center gap-2 text-sm"
            >
              <FileText className="w-4 h-4" />
              Generate Customer Report
            </Button>
            <Button 
              onClick={openDynamicsCRM}
              variant="outline" 
              className="flex items-center gap-2 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Dynamics CRM
            </Button>
            <Button 
              onClick={scheduleCheckupCall}
              variant="default" 
              className="flex items-center gap-2 text-sm"
            >
              <Phone className="w-4 h-4" />
              Schedule Check-up Call
            </Button>
          </div>
        </div>

        <div ref={pdfContentRef} className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 shadow-card">
              <div className="flex items-center gap-3">
                <Activity className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Sessions Last Month</p>
                  <p className="text-2xl font-bold">{site.numSessionsLastMonth}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 shadow-card">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-secondary" />
                <div>
                  <p className="text-sm text-muted-foreground">Sessions Last 12 Months</p>
                  <p className="text-2xl font-bold">{site.numSessionsOnServerLast12Month}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 shadow-card">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-success" />
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-bold">{site.activeUsers}</p>
                </div>
              </div>
            </Card>
          </div>

          <Separator />

          {/* System Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 shadow-card">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" />
                System Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MentorLearn Version:</span>
                  <span className="font-medium">{site.mlVersion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Database Version:</span>
                  <span className="font-medium">{site.dbVersion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Site Type:</span>
                  <Badge variant="outline">{site.siteType}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Connected Simulators:</span>
                  <span className="font-medium">{site.simulatorsConnected}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6 shadow-card">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Usage & Timeline
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Install Date:</span>
                  <span className="font-medium">{formatDate(site.siteInstallDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Usage:</span>
                  <span className="font-medium">{formatDate(site.lastUsage)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Months in Use:</span>
                  <span className="font-medium">{site.monthsInUse}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Used Last Month:</span>
                  <Badge variant={site.usedLastMonth ? "default" : "destructive"}>
                    {site.usedLastMonth ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
            </Card>
          </div>

          {/* License Information */}
          <Card className="p-6 shadow-card">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              {isExpiringSoon() && <AlertTriangle className="w-5 h-5 text-warning" />}
              License Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expiration Date:</span>
                  <span className={`font-medium ${isExpiringSoon() ? 'text-warning' : ''}`}>
                    {formatDate(site.siteLicenseExpirationInML)}
                  </span>
                </div>
                {isExpiringSoon() && (
                  <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                    <div className="flex items-center gap-2 text-warning">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">License expires soon!</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* User Statistics */}
          <Card className="p-6 shadow-card">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{site.registeredUsers}</p>
                <p className="text-sm text-muted-foreground">Registered Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-success">{site.activeUsers}</p>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-muted-foreground">{site.inactiveUsers}</p>
                <p className="text-sm text-muted-foreground">Inactive Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary">{site.activeUsers12Months}</p>
                <p className="text-sm text-muted-foreground">Active (12m)</p>
              </div>
            </div>
          </Card>

          {/* Simulation Statistics */}
          {siteSimulationStats.length > 0 && (
            <>
              <Separator />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 shadow-card">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Simulator Usage
                  </h3>
                  <div className="space-y-4 max-h-64 overflow-y-auto">
                    {simulatorUsage.map((usage, index) => (
                      <div key={index} className="border-l-4 border-primary pl-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">{usage.simulator}</span>
                          <Badge variant="secondary">{usage.sessions} sessions</Badge>
                        </div>
                        <div className="space-y-1">
                          {usage.modules.slice(0, 3).map((module: any, idx: number) => (
                            <div key={idx} className="flex justify-between text-sm text-muted-foreground">
                              <span>{module.module}</span>
                              <span>{module.sessions}</span>
                            </div>
                          ))}
                          {usage.modules.length > 3 && (
                            <p className="text-xs text-muted-foreground">
                              +{usage.modules.length - 3} more modules
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6 shadow-card">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Module Usage Chart
                  </h3>
                  <div className="h-64">
                    {barChartData.length > 0 ? (
                      <div className="space-y-2 h-full overflow-y-auto">
                        {barChartData
                          .filter(item => item.value > 0)
                          .sort((a, b) => b.value - a.value)
                          .slice(0, 8)
                          .map((item, index) => {
                            const maxValue = Math.max(...barChartData.map(d => d.value));
                            const percentage = (item.value / maxValue) * 100;
                            const moduleName = item.text.replace(/.*?- /, '').substring(0, 40);
                            const simulator = siteSimulationStats.find(s => s.moduleDescription === item.text)?.simulatorType || '';
                            
                            return (
                              <div key={index} className="space-y-1">
                                <div className="flex justify-between items-center text-sm">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-foreground truncate">{moduleName}</div>
                                    <div className="text-xs text-muted-foreground truncate">{simulator}</div>
                                  </div>
                                  <span className="text-sm font-medium text-primary ml-2">{item.value}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div 
                                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No simulation data available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* Location & Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 shadow-card">
              <h3 className="text-lg font-semibold mb-4">Location</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Country:</span>
                  <span className="font-medium">{site.country}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Region:</span>
                  <span className="font-medium">{site.region}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">W Region:</span>
                  <span className="font-medium">{site.wRegion}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6 shadow-card">
              <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="font-medium">{site.customer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Site Type:</span>
                  <Badge variant="outline">{site.siteType}</Badge>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};